// Public localization qualification page. Token-gated (?t=<token>), no login.
// Four bucket cards + a channel card, each independently savable. Matches the
// Inquire.tsx register exactly. White-label: never mentions a vendor.
// Mobile-first (375px).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { qualApi, type QualState } from '../../lib/api';
import { Turnstile } from '../../components/Turnstile';
import { Pegasus } from '../../components/DreamlinkMark';

const CSS = `
.ql{--cream:#F4F0E9;--paper:#FBF8F2;--ink:#2a201e;--brown:#6F5C53;--maroon:#7a1018;--maroon-deep:#4a0c11;--line:rgba(42,32,30,.16);
  --disp:'Archivo',-apple-system,system-ui,sans-serif;--sans:'DM Sans',-apple-system,system-ui,sans-serif;
  min-height:100vh;background:var(--cream);color:var(--ink);font-family:var(--sans);font-weight:300}
.ql a{color:var(--maroon);text-decoration:none}
.qlnav{display:flex;align-items:center;justify-content:space-between;padding:20px 24px}
.qlnav .mark{display:inline-flex;align-items:center;gap:9px;font-family:var(--disp);font-weight:700;font-size:16px;color:var(--maroon)}
.qlnav .mark i{font-weight:400;opacity:.7;font-style:normal;color:var(--ink)}
.qlwrap{max-width:620px;margin:0 auto;padding:8px 20px 80px}
.qlhead h1{font-family:var(--disp);font-weight:600;font-size:clamp(26px,5vw,34px);letter-spacing:-.02em;line-height:1.08;margin:18px 0 8px}
.qlhead p{font-size:15px;color:var(--brown);line-height:1.6;margin:0 0 8px}
.qlprog{font-family:var(--disp);font-size:12px;font-weight:600;letter-spacing:.04em;color:var(--maroon);margin:18px 0}
.qlcard{background:var(--paper);border:1px solid var(--line);border-radius:12px;padding:20px;margin-bottom:14px}
.qlcard.done{border-color:var(--maroon);box-shadow:0 0 0 2px rgba(122,16,24,.08)}
.qlcard h2{font-family:var(--disp);font-weight:600;font-size:17px;margin:0 0 4px}
.qlcard .sub{font-size:13px;color:var(--brown);margin:0 0 14px;line-height:1.55}
.opt{display:flex;gap:10px;align-items:flex-start;padding:11px 12px;border:1px solid var(--line);border-radius:9px;margin-bottom:8px;cursor:pointer;background:#fff}
.opt.sel{border-color:var(--maroon);background:rgba(122,16,24,.04)}
.opt input{margin-top:3px}
.opt .ot{font-size:14px;color:var(--ink)}
.opt .oh{font-size:12px;color:var(--brown);margin-top:2px}
.ql label.l{display:block;font-family:var(--disp);font-size:12px;font-weight:600;color:var(--ink);margin:10px 0 6px}
.ql input.t,.ql textarea{width:100%;padding:11px 13px;background:#fff;color:var(--ink);border:1px solid var(--line);border-radius:9px;font-size:15px;font-family:var(--sans);outline:none}
.ql input.t:focus,.ql textarea:focus{border-color:var(--maroon)}
.qlbtn{margin-top:12px;padding:11px 22px;background:var(--maroon);color:var(--cream);border:none;border-radius:100px;font-family:var(--disp);font-weight:600;font-size:13px;cursor:pointer}
.qlbtn:disabled{opacity:.55;cursor:wait}
.qlsaved{font-size:12px;color:#2f7a3f;margin-left:10px}
.qlerr{color:#9b1922;font-size:13px;margin-top:8px}
.qldone{text-align:center;padding:60px 20px}
.qldone h1{font-family:var(--disp);font-weight:600;font-size:30px;margin-bottom:10px}
.qldone p{font-size:15px;color:var(--brown);line-height:1.6}
.muted{font-size:12px;color:var(--brown)}
`;

function token(): string {
  if (typeof location === 'undefined') return '';
  return new URLSearchParams(location.search).get('t') || '';
}

export function Qualify() {
  const t = token();
  const [data, setData] = useState<QualState | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [ts, setTs] = useState<string | null>(null);
  const onVerify = useCallback((tok: string) => setTs(tok), []);
  const onExpire = useCallback(() => setTs(null), []);

  const reload = useCallback(() => {
    qualApi.get(t).then(setData).catch((e: unknown) => setLoadErr(String(e)));
  }, [t]);

  useEffect(() => {
    if (t) reload();
    else setLoadErr('missing_token');
  }, [t, reload]);

  if (!t || loadErr === 'missing_token' || loadErr?.includes('qual_404')) {
    return (
      <Shell>
        <div className="qldone">
          <h1>Link not found</h1>
          <p>This link is not valid. If you need a new one, reply to our email and we will send it.</p>
        </div>
      </Shell>
    );
  }
  if (loadErr?.includes('qual_410')) {
    return (
      <Shell>
        <div className="qldone">
          <h1>Link expired</h1>
          <p>This link has expired. Reply to our email and we will send a fresh one.</p>
        </div>
      </Shell>
    );
  }
  if (loadErr) {
    return (
      <Shell>
        <div className="qldone">
          <p className="muted">Something went wrong loading this page. Please try refreshing.</p>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="qldone">
          <p className="muted">Loading...</p>
        </div>
      </Shell>
    );
  }

  if (
    data.state === 'packet_ready' ||
    data.state === 'routed_to_partner' ||
    data.state === 'partner_responded'
  ) {
    return (
      <Shell>
        <div className="qldone">
          <h1>Thank you.</h1>
          <p>Our team will review and be in touch with your localization quote.</p>
        </div>
      </Shell>
    );
  }

  const analyticsDone =
    (data.analytics_method === 'screenshots' && data.assets.length > 0) ||
    !!data.analytics_granted_at;

  return (
    <Shell>
      <div className="qlhead">
        <h1>A few details for your quote.</h1>
        <p>
          This takes about three minutes. Save each section as you go; you can come back to this
          link anytime.
        </p>
        <div className="qlprog">{data.completeness} of 4 ready</div>
      </div>

      <AnalyticsCard data={data} ts={ts} done={analyticsDone} onSaved={reload} />
      <ChoiceCard
        bucket="economics"
        title="How would you like to structure this?"
        value={data.economics_pref}
        ts={ts}
        onSaved={reload}
        options={[
          { value: 'fixed', label: 'Fixed fee' },
          { value: 'fixed_revshare', label: 'Fixed fee plus revenue share' },
          {
            value: 'everbloom',
            label: 'Partner-financed',
            help: 'Best for channels above a revenue threshold; we will confirm eligibility.',
          },
          { value: 'unsure', label: 'Not sure yet' },
        ]}
      />
      <ChoiceCard
        bucket="distribution"
        title="Where should the localized versions live?"
        value={data.distribution_pref}
        ts={ts}
        onSaved={reload}
        options={[
          { value: 'multitrack', label: 'Multi-track on my existing videos' },
          { value: 'net_new', label: 'New language channels' },
          { value: 'combination', label: 'A combination' },
          { value: 'unsure', label: 'Not sure yet' },
        ]}
      />
      <ChoiceCard
        bucket="assets"
        title="How ready are your source files?"
        value={data.assets_readiness}
        ts={ts}
        onSaved={reload}
        options={[
          { value: 'organized', label: 'Organized and ready to share' },
          { value: 'pull_from_youtube', label: 'Pull everything from my YouTube channel' },
          { value: 'unsure', label: 'Not sure yet' },
        ]}
      />
      <ChannelCard data={data} ts={ts} onSaved={reload} />

      <div style={{ marginTop: 18 }}>
        <Turnstile onVerify={onVerify} onExpire={onExpire} theme="light" />
      </div>
      <p className="muted" style={{ marginTop: 16 }}>
        This link is private to you.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="ql">
      <style>{CSS}</style>
      <nav className="qlnav">
        <Link to="/" className="mark">
          <Pegasus size={20} color="currentColor" /> Dreamlink <i>Studio</i>
        </Link>
      </nav>
      <div className="qlwrap">{children}</div>
    </div>
  );
}

function AnalyticsCard({
  data,
  ts,
  done,
  onSaved,
}: {
  data: QualState;
  ts: string | null;
  done: boolean;
  onSaved: () => void;
}) {
  const [method, setMethod] = useState(data.analytics_method ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [granted, setGranted] = useState(!!data.analytics_granted_at);

  async function pick(m: 'viewer_grant' | 'screenshots') {
    setMethod(m);
    setErr(null);
    try {
      await qualApi.bucket(token(), 'analytics', { method: m }, ts);
      onSaved();
    } catch (e) {
      setErr(humanError(e));
    }
  }

  async function confirmGrant() {
    setBusy(true);
    setErr(null);
    try {
      await qualApi.grant(token(), ts);
      setGranted(true);
      onSaved();
    } catch (e) {
      setErr(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr(null);
    try {
      await qualApi.upload(token(), f, ts);
      onSaved();
    } catch (e2) {
      setErr(humanError(e2));
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  }

  return (
    <div className={`qlcard${done ? ' done' : ''}`}>
      <h2>Show us how your channel is performing.</h2>
      <p className="sub">Pick one. This is how we scope and price the work.</p>
      <label className={`opt${method === 'viewer_grant' ? ' sel' : ''}`}>
        <input
          type="radio"
          name="analytics"
          checked={method === 'viewer_grant'}
          onChange={() => pick('viewer_grant')}
        />
        <span>
          <span className="ot">Add our analytics team as a viewer</span>
          <span className="oh">
            Add analytics@dreamlink.global as a limited viewer on your YouTube channel. Our team
            will confirm access on our side.
          </span>
        </span>
      </label>
      {method === 'viewer_grant' && (
        <div style={{ margin: '4px 0 8px 28px' }}>
          <label className="opt" style={{ border: 'none', background: 'transparent', padding: 0 }}>
            <input
              type="checkbox"
              checked={granted}
              disabled={granted || busy}
              onChange={confirmGrant}
            />
            <span className="ot">I have added analytics@dreamlink.global as a viewer.</span>
          </label>
        </div>
      )}
      <label className={`opt${method === 'screenshots' ? ' sel' : ''}`}>
        <input
          type="radio"
          name="analytics"
          checked={method === 'screenshots'}
          onChange={() => pick('screenshots')}
        />
        <span>
          <span className="ot">Upload screenshots</span>
          <span className="oh">
            YouTube Studio Advanced Mode, last 90 days: Geography, Audio Track, and Subtitles/CC,
            showing views and watch time. PNG or JPEG.
          </span>
        </span>
      </label>
      {method === 'screenshots' && (
        <div style={{ margin: '6px 0 4px 28px' }}>
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange={onFile}
            disabled={busy}
          />
          {data.assets.length > 0 && (
            <div className="muted" style={{ marginTop: 6 }}>
              {data.assets.length} uploaded
            </div>
          )}
        </div>
      )}
      {err && <p className="qlerr">{err}</p>}
    </div>
  );
}

function ChoiceCard({
  bucket,
  title,
  value,
  options,
  ts,
  onSaved,
}: {
  bucket: string;
  title: string;
  value: string | null;
  options: { value: string; label: string; help?: string }[];
  ts: string | null;
  onSaved: () => void;
}) {
  const [sel, setSel] = useState(value ?? '');
  const [err, setErr] = useState<string | null>(null);

  async function pick(v: string) {
    setSel(v);
    setErr(null);
    try {
      await qualApi.bucket(token(), bucket, v, ts);
      onSaved();
    } catch (e) {
      setErr(humanError(e));
    }
  }

  return (
    <div className={`qlcard${value ? ' done' : ''}`}>
      <h2>{title}</h2>
      {options.map((o) => (
        <label key={o.value} className={`opt${sel === o.value ? ' sel' : ''}`}>
          <input
            type="radio"
            name={bucket}
            checked={sel === o.value}
            onChange={() => pick(o.value)}
          />
          <span>
            <span className="ot">{o.label}</span>
            {o.help && <span className="oh">{o.help}</span>}
          </span>
        </label>
      ))}
      {err && <p className="qlerr">{err}</p>}
    </div>
  );
}

function ChannelCard({
  data,
  ts,
  onSaved,
}: {
  data: QualState;
  ts: string | null;
  onSaved: () => void;
}) {
  const [url, setUrl] = useState(data.channel_url ?? '');
  const [notes, setNotes] = useState(data.content_notes ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await qualApi.bucket(token(), 'channel', { channel_url: url, content_notes: notes }, ts);
      setSaved(true);
      onSaved();
    } catch (e) {
      setErr(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="qlcard">
      <h2>Your channel</h2>
      <p className="sub">Optional, but it helps us move faster.</p>
      <label className="l">Channel URL</label>
      <input
        className="t"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="youtube.com/@yourchannel"
      />
      <label className="l">How often do you publish?</label>
      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. two long-form videos a week"
      />
      <div>
        <button className="qlbtn" onClick={save} disabled={busy}>
          {busy ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className="qlsaved">Saved</span>}
      </div>
      {err && <p className="qlerr">{err}</p>}
    </div>
  );
}

function humanError(e: unknown): string {
  const s = String(e);
  if (s.includes('_409')) return 'This form is complete and locked. Our team has it.';
  if (s.includes('_410')) return 'This link has expired. Reply to our email for a fresh one.';
  if (s.includes('_403')) return 'Please complete the verification below and try again.';
  if (s.includes('upload_400'))
    return 'That file was not accepted. Use a PNG or JPEG under 25 MB.';
  return 'Something went wrong. Please try again.';
}
